import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Input, Select, Label, Field } from '@shared/ui/Input';
import {
  LOGIST_TRIP_STATUS_LABELS,
  LOGIST_WEIGHT_CHECK_LABELS,
  type LogistDriver,
  type LogistRollingStock,
  type LogistTrip,
  type LogistTripLoadingDetails,
  type LogistTripStatus,
} from '@entities/logistics';
import {
  useAssignLogistTripMutation,
  useSetLogistTripStatusMutation,
  useSetLogistTripLoadingDetailsMutation,
} from '@shared/api';

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
`;

const Card = styled.div`
  padding: ${({ theme }) => theme.spacing(5)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.surface};
  max-width: 900px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Row = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  align-items: center;
`;

const DocActionsRow = styled(Row)`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  text-align: left;
  padding: ${({ theme }) => theme.spacing(2)};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 600;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: ${({ theme }) => theme.spacing(2)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  vertical-align: middle;
`;

const SmallBtn = styled.button`
  font-size: 12px;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
`;

const MiniSelect = styled(Select)`
  font-size: 12px;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  min-width: 120px;
`;

const Msg = styled.div<{ $error?: boolean }>`
  margin-top: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme, $error }) =>
    $error ? theme.colors.status.danger : theme.colors.text.secondary};
`;

const Badge = styled.span<{ $tone: 'ok' | 'bad' | 'muted' }>`
  display: inline-block;
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(1.5)}`};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  color: #fff;
  background: ${({ theme, $tone }) =>
    $tone === 'ok'
      ? theme.colors.status.success
      : $tone === 'bad'
        ? theme.colors.status.danger
        : theme.colors.text.secondary};
`;

const EmptyTripsNotice = styled.div`
  opacity: 0.7;
`;

const OverloadHint = styled.div`
  font-size: 11px;
  margin-top: ${({ theme }) => theme.spacing(0.5)};
  opacity: 0.8;
`;

const DocHint = styled.div`
  font-size: 13px;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  line-height: 1.6;
  opacity: 0.85;
`;

function apiErr(e: unknown): string {
  if (e && typeof e === 'object' && 'data' in e) {
    const d = (e as { data?: unknown }).data;
    if (d && typeof d === 'object' && 'error' in d) return String((d as { error: unknown }).error);
  }
  return 'ошибка запроса';
}

function weightTone(c: LogistTrip['weight_check']): 'ok' | 'bad' | 'muted' {
  return c === 'passed' ? 'ok' : c === 'failed' ? 'bad' : 'muted';
}

export interface LogistTripsTableProps {
  trips: LogistTrip[];
  seriesCount: number;
  drivers: LogistDriver[];
  vehicles: LogistRollingStock[];
  trailers: LogistRollingStock[];
  containerTypes: { code: string; label: string; hint?: string }[];
  canWrite: boolean;
  weighTripId: string | null;
  onToggleWeigh: (trip: LogistTrip) => void;
  onDeleteTrip: (trip: LogistTrip) => void;
  onMessage?: (msg: string | null, isError?: boolean) => void;
}

export function LogistTripsTable({
  trips,
  seriesCount,
  drivers,
  vehicles,
  trailers,
  containerTypes,
  canWrite,
  weighTripId,
  onToggleWeigh,
  onDeleteTrip,
  onMessage,
}: LogistTripsTableProps) {
  const [docTripId, setDocTripId] = useState<string | null>(null);
  const [df, setDf] = useState<Record<string, string>>({});
  const [ok, setOk] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [assignTrip] = useAssignLogistTripMutation();
  const [setTripStatus] = useSetLogistTripStatusMutation();
  const [setLoadingDetails, loadingDetailsState] = useSetLogistTripLoadingDetailsMutation();

  const docTrip = docTripId ? (trips.find((t) => t.id === docTripId) ?? null) : null;

  const driverName = useMemo(() => {
    const m = new Map(drivers.map((d) => [d.id, d.full_name]));
    return (id: string | null) => (id ? (m.get(id) ?? '—') : '—');
  }, [drivers]);

  const plate = useMemo(() => {
    const m = new Map([...vehicles, ...trailers].map((v) => [v.id, v.plate_number]));
    return (id: string | null) => (id ? (m.get(id) ?? '—') : '—');
  }, [vehicles, trailers]);

  async function reassign(trip: LogistTrip, patch: Partial<Record<string, string | null>>) {
    const body = {
      driver_id: trip.driver_id,
      vehicle_id: trip.vehicle_id,
      trailer_id: trip.trailer_id,
      ...patch,
    };
    try {
      await assignTrip({ id: trip.id, body }).unwrap();
    } catch (e) {
      const msg = `Не удалось назначить: ${apiErr(e)}`;
      setErr(msg);
      onMessage?.(msg, true);
    }
  }

  function startDocDetails(t: LogistTrip) {
    if (docTripId === t.id) {
      setDocTripId(null);
      return;
    }
    const at = (v: string | null) => (v ? v.slice(0, 16) : '');
    setDf({
      stated_arrival_at: at(t.stated_arrival_at),
      actual_arrival_at: at(t.actual_arrival_at),
      actual_departure_at: at(t.actual_departure_at),
      cargo_condition: t.cargo_condition ?? '',
      cargo_package_method: t.cargo_package_method ?? '',
      cargo_container_type: t.cargo_container_type ?? '',
      cargo_marks: t.cargo_marks ?? '',
      actual_cargo_condition: t.actual_cargo_condition ?? '',
      actual_package_condition: t.actual_package_condition ?? '',
      actual_vehicle_body_condition: t.actual_vehicle_body_condition ?? '',
    });
    setDocTripId(t.id);
    setOk(null);
    setErr(null);
  }

  function dset(k: string, v: string) {
    setDf((s) => ({ ...s, [k]: v }));
  }

  async function submitDocDetails() {
    if (!docTripId) return;
    setErr(null);
    setOk(null);
    const code = (df.cargo_container_type ?? '').trim();
    if (code && code.length !== 2) {
      setErr('Вид тары — двузначный код из списка.');
      return;
    }
    const body: { id: string } & LogistTripLoadingDetails = { id: docTripId };
    for (const key of [
      'cargo_condition',
      'cargo_package_method',
      'cargo_container_type',
      'cargo_marks',
      'actual_cargo_condition',
      'actual_package_condition',
      'actual_vehicle_body_condition',
    ]) {
      const raw = (df[key] ?? '').trim();
      if (raw) body[key as keyof LogistTripLoadingDetails] = raw;
    }
    for (const key of ['stated_arrival_at', 'actual_arrival_at', 'actual_departure_at']) {
      const raw = (df[key] ?? '').trim();
      if (raw) body[key as keyof LogistTripLoadingDetails] = new Date(raw).toISOString();
    }
    try {
      await setLoadingDetails(body).unwrap();
      setOk('Сведения для накладной сохранены.');
    } catch (x) {
      setErr(`Не удалось: ${apiErr(x)}`);
    }
  }

  return (
    <>
      <Section>
        <SectionTitle>
          Рейсы заказа{seriesCount > 0 ? ` · серий: ${seriesCount}` : ''}
        </SectionTitle>
        {trips.length === 0 ? (
          <EmptyTripsNotice>Рейсов по заказу пока нет.</EmptyTripsNotice>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>№</Th>
                <Th>Водитель</Th>
                <Th>ТС</Th>
                <Th>Прицеп</Th>
                <Th>План, кг</Th>
                <Th>Факт, кг</Th>
                <Th>Контроль</Th>
                <Th>Статус</Th>
                {canWrite && <Th>Действия</Th>}
              </tr>
            </thead>
            <tbody>
              {trips.map((t) => (
                <tr key={t.id}>
                  <Td>{t.seq_no ?? '—'}</Td>
                  <Td>
                    {canWrite ? (
                      <MiniSelect
                        value={t.driver_id ?? ''}
                        onChange={(e) => reassign(t, { driver_id: e.target.value || null })}
                      >
                        <option value="">—</option>
                        {drivers.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.full_name}
                          </option>
                        ))}
                      </MiniSelect>
                    ) : (
                      driverName(t.driver_id)
                    )}
                  </Td>
                  <Td>
                    {canWrite ? (
                      <MiniSelect
                        value={t.vehicle_id ?? ''}
                        onChange={(e) => reassign(t, { vehicle_id: e.target.value || null })}
                      >
                        <option value="">—</option>
                        {vehicles.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.plate_number}
                          </option>
                        ))}
                      </MiniSelect>
                    ) : (
                      plate(t.vehicle_id)
                    )}
                  </Td>
                  <Td>{plate(t.trailer_id)}</Td>
                  <Td>{t.planned_mass_kg ?? '—'}</Td>
                  <Td>{t.actual_mass_kg ?? '—'}</Td>
                  <Td>
                    <Badge $tone={weightTone(t.weight_check)}>
                      {LOGIST_WEIGHT_CHECK_LABELS[t.weight_check]}
                    </Badge>
                    {t.weight_check === 'failed' && t.overload_kg != null && (
                      <OverloadHint>+{t.overload_kg} кг</OverloadHint>
                    )}
                  </Td>
                  <Td>
                    {canWrite ? (
                      <MiniSelect
                        value={t.status}
                        onChange={(e) =>
                          setTripStatus({ id: t.id, status: e.target.value }).unwrap().catch((x) => {
                            const msg = `Не удалось: ${apiErr(x)}`;
                            setErr(msg);
                            onMessage?.(msg, true);
                          })
                        }
                      >
                        {(Object.keys(LOGIST_TRIP_STATUS_LABELS) as LogistTripStatus[]).map((s) => (
                          <option key={s} value={s}>
                            {LOGIST_TRIP_STATUS_LABELS[s]}
                          </option>
                        ))}
                      </MiniSelect>
                    ) : (
                      LOGIST_TRIP_STATUS_LABELS[t.status]
                    )}
                  </Td>
                  {canWrite && (
                    <Td>
                      <Row>
                        <SmallBtn onClick={() => onToggleWeigh(t)}>
                          {weighTripId === t.id ? 'Скрыть' : 'Взвесить'}
                        </SmallBtn>
                        <SmallBtn onClick={() => startDocDetails(t)}>
                          {docTripId === t.id ? 'Скрыть' : 'Для накладной'}
                        </SmallBtn>
                        <SmallBtn onClick={() => onDeleteTrip(t)}>Удалить</SmallBtn>
                      </Row>
                    </Td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      {docTrip && canWrite && (
        <Section>
          <SectionTitle>Сведения для накладной · рейс № {docTrip.seq_no ?? '—'}</SectionTitle>
          <Card>
            <DocHint>
              Эти сведения обязательны по формату электронной накладной. Без них выпуск
              отказывает: оператор проверяет документ по схеме налоговой и отвергает
              целиком, не называя, чего не хватило.
            </DocHint>
            <Grid>
              <Field>
                <Label>Подача под погрузку (по заявке)</Label>
                <Input
                  type="datetime-local"
                  value={df.stated_arrival_at ?? ''}
                  onChange={(e) => dset('stated_arrival_at', e.target.value)}
                />
              </Field>
              <Field>
                <Label>Фактически прибыл</Label>
                <Input
                  type="datetime-local"
                  value={df.actual_arrival_at ?? ''}
                  onChange={(e) => dset('actual_arrival_at', e.target.value)}
                />
              </Field>
              <Field>
                <Label>Фактически убыл</Label>
                <Input
                  type="datetime-local"
                  value={df.actual_departure_at ?? ''}
                  onChange={(e) => dset('actual_departure_at', e.target.value)}
                />
              </Field>
              <Field>
                <Label>Состояние груза</Label>
                <Input
                  value={df.cargo_condition ?? ''}
                  placeholder="Соответствует требованиям перевозки"
                  onChange={(e) => dset('cargo_condition', e.target.value)}
                />
              </Field>
              <Field>
                <Label>Способ упаковки</Label>
                <Input
                  value={df.cargo_package_method ?? ''}
                  placeholder="Без упаковки, навалом"
                  onChange={(e) => dset('cargo_package_method', e.target.value)}
                />
              </Field>
              <Field>
                <Label>Вид тары</Label>
                <Select
                  value={df.cargo_container_type ?? ''}
                  onChange={(e) => dset('cargo_container_type', e.target.value)}
                >
                  <option value="">— не выбран —</option>
                  {containerTypes.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} — {c.label} ({c.hint})
                    </option>
                  ))}
                </Select>
              </Field>
              <Field>
                <Label>Маркировка</Label>
                <Input
                  value={df.cargo_marks ?? ''}
                  placeholder="Без маркировки"
                  onChange={(e) => dset('cargo_marks', e.target.value)}
                />
              </Field>
              <Field>
                <Label>Состояние груза при приёме</Label>
                <Input
                  value={df.actual_cargo_condition ?? ''}
                  onChange={(e) => dset('actual_cargo_condition', e.target.value)}
                />
              </Field>
              <Field>
                <Label>Состояние упаковки при приёме</Label>
                <Input
                  value={df.actual_package_condition ?? ''}
                  onChange={(e) => dset('actual_package_condition', e.target.value)}
                />
              </Field>
              <Field>
                <Label>Состояние кузова при приёме</Label>
                <Input
                  value={df.actual_vehicle_body_condition ?? ''}
                  onChange={(e) => dset('actual_vehicle_body_condition', e.target.value)}
                />
              </Field>
            </Grid>
            <DocActionsRow>
              <Button onClick={submitDocDetails} disabled={loadingDetailsState.isLoading}>
                Сохранить
              </Button>
              <Button $variant="secondary" onClick={() => setDocTripId(null)}>
                Закрыть
              </Button>
            </DocActionsRow>
            {ok && <Msg>{ok}</Msg>}
            {err && <Msg $error>{err}</Msg>}
          </Card>
        </Section>
      )}
    </>
  );
}
